import { AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogOverlay, Box, Button, Collapse, Flex, Text, Tooltip, useDisclosure } from '@chakra-ui/react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useContext, useRef } from 'react';

import { AppContext } from '@context';
import { isRenderingStale } from '@helpers';
import { RenderingCaptureBox } from '@components';
import { selectNavigation, selectRenderingDirty, selectSaveAsDraftClose, setSaveAsDraftClose, useAppDispatch, useAppSelector } from '@state';
import { useDesign } from '@hooks';

export const SaveAsDraftAlert = () => {
	const dispatch = useAppDispatch();
	const { isOpen } = useAppSelector(selectSaveAsDraftClose);
	const appNav = useAppSelector(selectNavigation);
	const { appState: { customerDesigns: designs }} = useContext(AppContext);
	const [, design] = useDesign();

	const cancelRef = useRef<any>(undefined);
	const onClose = (confirmed: boolean | null) => dispatch(setSaveAsDraftClose({ isOpen: false, confirmed }));
	const renderingDirty = useAppSelector(selectRenderingDirty);

	const tdController = design?.tdController ?? null;

	// Check if current version is a draft (version === null)
	const isDraft = design?.blueprint?.kitchens?.getActiveVersion()?.version === null;
	// Check if there are changes (kitchen OR rendering)
	const currentDesign = designs.designs.find((bp) => bp.blueprint.id === appNav.designId) ?? null;
	const hasChanges = !!(currentDesign?.isDirty) || renderingDirty;
	// Disable Draft button if it's a draft but has no changes
	const isDraftButtonDisabled = isDraft && !hasChanges;

	const stale = isRenderingStale({ design });

	const { isOpen: renderOpen, onToggle: toggleRender } = useDisclosure({ defaultIsOpen: false });

	return (<>
		<AlertDialog
			isOpen={isOpen}
			leastDestructiveRef={cancelRef}
			onClose={() => onClose(null)}
		>
			<AlertDialogOverlay>
				<AlertDialogContent>
					<AlertDialogHeader fontSize="lg" fontWeight="bold">
						Save as...
					</AlertDialogHeader>

					<AlertDialogBody>
						{isDraftButtonDisabled ? (
							<Text>No changes to save. Create a new version to save the current draft?</Text>
						) : (
							<Text>Save as a draft or create a new version?</Text>
						)}

						{tdController && (
							<Box mt={3} borderTop="1px solid" borderColor="gray.200" pt={2}>
								<Flex align="center" cursor="pointer" onClick={toggleRender} userSelect="none" gap={1}>
									{renderOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
									<Text fontSize="sm" fontWeight="medium">Rendering</Text>
									{stale && (
										<Box ml={1} w="9px" h="9px" bg="orange.400" borderRadius="full" border="2px solid" borderColor="white" />
									)}
								</Flex>
								<Collapse in={renderOpen} animateOpacity>
									<RenderingCaptureBox tdController={tdController} active={renderOpen} />
								</Collapse>
							</Box>
						)}
					</AlertDialogBody>

					<AlertDialogFooter>
						<Tooltip
							label={isDraftButtonDisabled ? 'No changes to save to draft' : ''}
							isDisabled={!isDraftButtonDisabled}
						>
							<Button
								colorScheme="blue"
								ref={cancelRef}
								onClick={() => onClose(true)}
								isDisabled={isDraftButtonDisabled}
							>
								Draft
							</Button>
						</Tooltip>
						<Button colorScheme="green" onClick={() => onClose(false)} ml={3}>
							New Version
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialogOverlay>
		</AlertDialog>
	</>);
};
